import React, { useState, useEffect } from 'react';
import {
    Modal, Form, Input, Button, message, Space, Typography, Divider, Row, Col, DatePicker
} from 'antd';
import { SaveOutlined, SwapOutlined } from '@ant-design/icons';
import axios from '../offline/profileApi';
import { isProfileQueued } from '../offline/profileActions';
import API from '../config/api';
import { useDoctorContext } from './DoctorContext';
import moment from 'moment';

const { Text } = Typography;

interface Patient {
    _id: string;
    patient_name: string;
    patient_phone: string;
    age: string;
    address?: string;
    [key: string]: any;
}

interface ReferralModalProps {
    visible: boolean;
    onCancel: () => void;
    patient: Patient | null;
    /** When set, the modal updates this referral (PATCH) instead of creating a new one. */
    referralToEdit?: any | null;
    latestDiagnosis?: string;
    onReferralSaved?: () => void;
}

const ReferralModal: React.FC<ReferralModalProps> = ({
    visible,
    onCancel,
    patient,
    referralToEdit = null,
    latestDiagnosis = '',
    onReferralSaved
}) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);

    const { settings: doctorSettings, doctorId } = useDoctorContext();

    const doctorDisplayName =
        doctorSettings.doctorTitle ||
        doctorSettings.clinicName ||
        localStorage.getItem('doctorName') ||
        '';

    const doctorClinicName = doctorSettings.clinicName || '';
    const doctorPhone = doctorSettings.clinicPhone || '';

    useEffect(() => {
        if (!visible) return;
        form.resetFields();
        if (referralToEdit?.referral_id) {
            form.setFieldsValue({
                from_doctor_name: referralToEdit.from_doctor_name ?? doctorDisplayName,
                from_doctor_title: referralToEdit.from_doctor_title ?? '',
                from_clinic_name: referralToEdit.from_clinic_name ?? doctorClinicName,
                from_doctor_phone: referralToEdit.from_doctor_phone ?? doctorPhone,
                referral_date: referralToEdit.referral_date ? moment(referralToEdit.referral_date) : moment(),
                to_doctor_name: referralToEdit.to_doctor_name ?? '',
                to_doctor_title: referralToEdit.to_doctor_title ?? '',
                to_clinic_name: referralToEdit.to_clinic_name ?? '',
                subject: referralToEdit.subject ?? '',
                diagnosis: referralToEdit.diagnosis ?? latestDiagnosis,
                referral_body: referralToEdit.referral_body ?? '',
                signature: referralToEdit.signature ?? '',
            });
        } else {
            form.setFieldsValue({
                from_doctor_name: doctorDisplayName,
                from_clinic_name: doctorClinicName,
                from_doctor_phone: doctorPhone,
                referral_date: moment(),
                diagnosis: latestDiagnosis,
                subject: patient
                    ? `Referral of Patient – ${patient.patient_name}`
                    : 'Patient Referral',
            });
        }
    }, [visible, form, doctorDisplayName, doctorClinicName, doctorPhone, patient, referralToEdit, latestDiagnosis]);

    // The in-modal print buttons were removed (the saved-referral list in
    // PatientDetail.tsx is the sole entry point for printing referral
    // letters, and it uses the same printHtml() utility from a clean user
    // gesture). The buildReferralPrintHtml helper that used to live here
    // has been deleted with them.

    const handleSave = async (values: any): Promise<void> => {
        if (!patient) return;
        try {
            setIsLoading(true);
            const payload = {
                referral_date: values.referral_date
                    ? values.referral_date.toISOString()
                    : new Date().toISOString(),
                from_doctor_name: values.from_doctor_name || '',
                from_doctor_title: values.from_doctor_title || '',
                from_clinic_name: values.from_clinic_name || '',
                from_doctor_phone: values.from_doctor_phone || '',
                to_doctor_name: values.to_doctor_name || '',
                to_doctor_title: values.to_doctor_title || '',
                to_clinic_name: values.to_clinic_name || '',
                subject: values.subject || '',
                diagnosis: values.diagnosis || '',
                referral_body: values.referral_body || '',
                signature: values.signature || values.from_doctor_name || '',
                doctor_id: doctorId || localStorage.getItem('doctorId') || '',
            };

            const referralId = referralToEdit?.referral_id;
            const response = referralId
                ? await axios.patch(
                    `${API.BASE_URL}${API.ENDPOINTS.REFERRAL_ITEM(patient._id, referralId)}`,
                    payload
                )
                : await axios.post(
                    `${API.BASE_URL}${API.ENDPOINTS.REFERRALS(patient._id)}`,
                    payload
                );

            if (response.data?.success) {
                message.success(referralId ? 'Referral letter updated successfully' : 'Referral letter saved successfully');
                onReferralSaved?.();
                onCancel();
                return;
            }
            message.error(referralId ? 'Failed to update referral letter' : 'Failed to save referral letter');
        } catch (error) {
            if (isProfileQueued(error)) { message.warning('Saved on this device — pending sync.'); onCancel(); return; }
            console.error('Error saving referral:', error);
            message.error(referralToEdit?.referral_id ? 'Failed to update referral letter' : 'Failed to save referral letter');
        } finally {
            setIsLoading(false);
        }
    };

    // NOTE: the in-modal Print / Save&Print buttons (and the
    // buildReferralPrintHtml helper that fed them) were removed. Printing
    // is done exclusively from the saved-referral list in
    // PatientDetail.tsx, which builds its own HTML and calls printHtml()
    // from a clean (no open modal) user gesture — that path works
    // reliably on every device including Samsung tablets. If you ever
    // reintroduce in-modal print, call printHtml() synchronously from
    // the click handler — never after an await or inside a setTimeout.

    return (
        <Modal
            title={
                <Space>
                    <SwapOutlined style={{ color: '#1890ff' }} />
                    <span>{referralToEdit?.referral_id ? 'Edit referral / تعديل إحالة' : 'Referral Letter / خطاب إحالة'}</span>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={720}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
            >
                {/* Date */}
                <Form.Item name="referral_date" label="Date / التاريخ">
                    <DatePicker style={{ width: '100%' }} format="D MMMM YYYY" />
                </Form.Item>

                {/* FROM fields kept hidden — auto-filled from doctor settings and saved to DB */}
                <Form.Item name="from_doctor_name" hidden><Input /></Form.Item>
                <Form.Item name="from_doctor_title" hidden><Input /></Form.Item>
                <Form.Item name="from_clinic_name" hidden><Input /></Form.Item>
                <Form.Item name="from_doctor_phone" hidden><Input /></Form.Item>

                <Divider orientation="left">
                    <Text type="secondary" style={{ fontSize: 12 }}>TO / إلى</Text>
                </Divider>

                <Row gutter={16}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="to_doctor_name"
                            label="Referred To (Doctor Name)"
                            rules={[{ required: true, message: 'Please enter the referred doctor name' }]}
                        >
                            <Input placeholder="Dr. Mohamed Ali" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="to_doctor_title" label="Title / Specialty">
                            <Input placeholder="Consultant Cardiologist" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item name="to_clinic_name" label="Clinic / Hospital">
                            <Input placeholder="Heart Care Center" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left">
                    <Text type="secondary" style={{ fontSize: 12 }}>LETTER / الرسالة</Text>
                </Divider>

                {/* Subject */}
                <Form.Item name="subject" label="Subject / الموضوع">
                    <Input placeholder={`Referral of Patient – ${patient?.patient_name || ''}`} />
                </Form.Item>

                <Form.Item name="diagnosis" label="Diagnosis / التشخيص">
                    <Input.TextArea rows={2} placeholder="Patient diagnosis" />
                </Form.Item>

                {/* Patient summary auto-hint */}
                {patient && (
                    <div style={{
                        background: '#f6f8fa',
                        border: '1px solid #e0e7ef',
                        borderRadius: 6,
                        padding: '10px 14px',
                        marginBottom: 12,
                        fontSize: 12,
                        color: '#555'
                    }}>
                        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                            Patient Info (for reference):
                        </Text>
                        <strong>{patient.patient_name}</strong>
                        {patient.age ? `, ${patient.age}` : ''}
                        {patient.patient_phone ? ` · ${patient.patient_phone}` : ''}
                        {patient.address ? ` · ${patient.address}` : ''}
                    </div>
                )}

                <Form.Item
                    name="referral_body"
                    label="Referral Details / تفاصيل الإحالة"
                    rules={[{ required: true, message: 'Please enter referral details' }]}
                >
                    <Input.TextArea
                        rows={8}
                        placeholder={
                            `I am referring my patient, ${patient?.patient_name || '[Patient Name]'}, for further evaluation and management.\n\nThe patient has been experiencing...\n\nMedical history includes...\n\nCurrent medications include:\n  - ...\n\nI would appreciate your expert assessment and recommendations.`
                        }
                        style={{ fontSize: 13 }}
                    />
                </Form.Item>

                {/* Signature */}
                <Form.Item name="signature" label="Signature / التوقيع">
                    <Input placeholder="Dr. Name / Signature" />
                </Form.Item>

                {/* Action Buttons */}
                {/* "Print Only" and "Save & Print" were removed from this
                    modal because the in-modal print path was unreliable on
                    Samsung tablets. The user prints from the saved-referral
                    list in PatientDetail instead, which uses the same
                    iframe overlay flow but from a clean (no open modal)
                    user gesture and works correctly across all devices. */}
                <Form.Item style={{ marginBottom: 0 }}>
                    <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={isLoading}
                            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                        >
                            {referralToEdit?.referral_id ? 'Update Referral' : 'Save Referral'}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ReferralModal;
